import React, { useState } from "react";
import PropTypes from "prop-types";

import {
    RadioButton,
    Container,
    CheckedIndicator,
    Price,
    Subscription,
    IconFlexBox,
    IconContainer,
    Content,
    Title,
    Details,
    Detail,
    HideOnMobile
} from "./styles";
import Badge from "./Badge";
import { ButtonGroup } from "core/ButtonGroup";
import { contentTypes, proSubscriptionTerms } from "core/props";
import colors from "core/colors";
import CheckedIcon from "core/ReusableIcons/Success";
import UncheckedIcon from "core/ReusableIcons/Circle";
import { KEY_BASIC_RES } from "photos/helpers";
import Flexbox from "core/Flexbox";

const LicenseButton = ({ type, schemas }) => {
    // NOTE
    // - `selectedIndex` is which license is currently selected. Sometimes,
    //   none of the `schemas` are selected because a different LicenseButton
    //   is selected.
    // - `activeIndex` is which `schema` option is being displayed. We either
    //   display the selected schema, a previously selected schema, or the first
    //   schema (in that order).
    const selectedIndex = schemas.reduce(
        (selectedIndex, { selected }, i) => (selected ? i : selectedIndex),
        0
    );
    const [activeIndex, setActiveIndex] = useState(selectedIndex);
    const visible = schemas[activeIndex];
    const { meta, link } = visible;

    let details = null;
    if (type === "photo") {
        details = (
            <>
                <Detail key="dimensions">
                    {visible.backendKey === KEY_BASIC_RES && `${meta.mp} `}
                    {meta.pixelDimensions}
                    <HideOnMobile>
                        , {meta.printDimensions} @ {meta.dpi}
                    </HideOnMobile>
                </Detail>
                <Detail key="link">{link}</Detail>
                {visible.backendKey !== KEY_BASIC_RES && (
                    <Detail key="support">
                        <HideOnMobile>Purchase supports artist</HideOnMobile>
                    </Detail>
                )}
            </>
        );
    } else if (type === "icon") {
        details = <Detail>{meta.message}</Detail>;
    }

    // NOTE
    // `RadioButton` is not visible, but gives us native accessibility support.
    // `CheckedIndicator` is the visual signal of whether the option is selected.
    return (
        <label
            aria-label={visible.title}
            htmlFor={visible.id}
            onClick={visible.onClick}
            data-testid={`license-label-${visible.id}`}
        >
            <RadioButton
                id={visible.id}
                checked={visible.selected}
                onChange={visible.onClick}
            />
            <Container activeOption={activeIndex}>
                <Flexbox flexDirection="row" justifyContent="space-between">
                    <CheckedIndicator>
                        {visible.selected ? (
                            <CheckedIcon
                                size="md"
                                fillColor={colors.accent.darkMode.suenosBuenos}
                            />
                        ) : (
                            <UncheckedIcon
                                size="md"
                                fillColor={colors.secondary.manatee}
                            />
                        )}
                    </CheckedIndicator>

                    <Content>
                        <Title>{visible.title}</Title>
                        {!!visible.badge && <Badge>{visible.badge}</Badge>}
                        {schemas.length > 1 && (
                            <ButtonGroup
                                buttons={schemas.map((schema, i) => ({
                                    isActive: i === activeIndex,
                                    label: schema.optionName,
                                    onClick(e) {
                                        e.stopPropagation();
                                        setActiveIndex(i);
                                        schema.onClick();
                                    }
                                }))}
                            />
                        )}
                    </Content>

                    <Price>
                        {visible.price}
                        {!!visible.subscriptionTerm && (
                            <Subscription>
                                {visible.subscriptionTerm}
                            </Subscription>
                        )}
                    </Price>
                </Flexbox>
                <Flexbox flexDirection="row">
                    <Details>{details}</Details>
                    {!!visible.icon && (
                        <IconFlexBox>
                            <IconContainer>{visible.icon}</IconContainer>
                        </IconFlexBox>
                    )}
                </Flexbox>
            </Container>
        </label>
    );
};

LicenseButton.propTypes = {
    type: PropTypes.oneOf(contentTypes).isRequired,
    schemas: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            backendKey: PropTypes.string.isRequired,
            selected: PropTypes.bool.isRequired,
            title: PropTypes.string.isRequired,
            badge: PropTypes.string, // badge text just below title
            price: PropTypes.string.isRequired,
            subscriptionTerm: PropTypes.oneOf(proSubscriptionTerms), // duration of time if the price is a subscription; example - "year"
            optionName: PropTypes.string, // if there are nested license options, this will be the switch label
            meta: PropTypes.object.isRequired,
            link: PropTypes.element,
            icon: PropTypes.element, // icon printed in bottom-right corner of button
            onClick: PropTypes.func.isRequired
        })
    ).isRequired
};

export default LicenseButton;
